import React from 'react';
import type { SearchResultWithUIState } from './types';
import { NumericSpan, PercentageSpan } from 'venn-components';
import {
  iconsRender,
  renderExtrapolation,
  renderProxyType,
  renderSharpeRatio,
  renderTaxStatus,
  renderTimespan,
  renderTotalReturn,
  renderVolatility,
} from './Shared';
import { Pill } from 'venn-ui-kit';

const renderAlpha = (item: SearchResultWithUIState) => {
  const { annualizedAlphaTStat, periodAlpha, periodAnnualized } = item;
  if (periodAlpha && annualizedAlphaTStat) {
    return (
      <>
        <p>Residual Return</p>
        <p>
          / T-Stat:
          <span className="tooltip-value">
            <PercentageSpan value={periodAlpha} />
            &nbsp;/&nbsp;
            <NumericSpan precision={2}>{annualizedAlphaTStat}</NumericSpan>
          </span>
        </p>
      </>
    );
  }

  if (!periodAlpha && annualizedAlphaTStat) {
    return (
      <p>
        T-Stat:&nbsp;
        <NumericSpan className="tooltip-value" precision={2}>
          {annualizedAlphaTStat}
        </NumericSpan>
      </p>
    );
  }

  if (!annualizedAlphaTStat && periodAlpha) {
    return (
      <p>
        {periodAnnualized ? 'Annualized' : 'Cumulative'} Residual Return:&nbsp;
        <PercentageSpan className="tooltip-value" value={periodAlpha} />
      </p>
    );
  }

  return null;
};

const renderText = (item: SearchResultWithUIState) => {
  if (!item.fundId && !item.portfolioId) {
    return '';
  }
  if (item.investmentSource === 'CUSTOM') {
    return 'Composite Benchmark';
  }
  if (item.portfolioId) {
    return 'Portfolio';
  }
  return 'Investment';
};

interface ItemOverviewProps {
  item: SearchResultWithUIState;
}

const ItemOverview = ({ item }: ItemOverviewProps) => {
  /* Even though an item has a proxy, there are cases wherein the proxy wasn't used to calculate
   * the metrics displayed in the library, so we don't show the proxy info here.
   * Examples: system funds and publisher funds since they are shared across workspaces but their
   * proxies are workspace-specific.
   */
  const renderProxyInfo = !!(item.proxyStartRange && item.proxyEndRange);
  return (
    <div>
      {item.symbol && <Pill className="text-venn-black bg-venn-white inline-block mb-[4px]">{item.symbol}</Pill>}
      <h1>
        <span className="qa-library-item-name">{item.name}</span>
        <div className="text-[10px] font-bold">
          {iconsRender(item)} {renderText(item)}
        </div>
      </h1>

      {item.portfolioId && renderTimespan('Availability', item.startRange, item.endRange, item.frequency)}
      {item.taxStatuses && renderTaxStatus(item)}
      {item.fundId &&
        renderTimespan('Availability', item.unproxiedStartRange, item.unproxiedEndRange, item.unproxiedFrequency)}
      {renderProxyInfo && renderProxyType(item.proxyType)}
      {renderProxyInfo && item.proxyType !== 'EXTRAPOLATE' && renderExtrapolation(item.extrapolated ?? false)}
      {renderProxyInfo && renderTimespan('Proxy Range', item.proxyStartRange, item.proxyEndRange, item.frequency, true)}
      {renderTotalReturn(item)}
      {renderVolatility(item)}
      {renderSharpeRatio(item)}
      {renderAlpha(item)}

      <div className="mt-[15px] italic">Click to perform analysis.</div>
    </div>
  );
};

export default ItemOverview;
